import {useTranslation} from '@/translations/i18n.tsx'
import {useMe} from '@features/user/queries/useMe.ts'
import {raise} from '@utilities/helpers.ts'
import {CategoryCard} from './category-card'
import {AlertCircleIcon, Briefcase01Icon, PlusIcon} from '@components/ui/icon'
import {Switch} from '@components/ui/switch'
import {useChangeFreelancerAvailability} from '@features/settings/queries/useChangeFreelancerAvailability'
import {useRef, useState} from 'react'
import {AreaItem} from './area-item'
import {SettingsCard} from './settings-card'
import {retrieveAddressDataFromComponents} from '@features/settings/utils'
import {Coordinates} from '@features/settings/types'
import {useChangeFreelancerAddress} from '@features/settings/queries/useChangeFreelancerAddress'
import {useDeleteFreelancerArea} from '@features/settings/queries/useDeleteFreelancerArea'
import {AddAreasModal} from '@features/settings/components/add-areas-modal'
import {Spinner} from '@components/ui/spinner'
import {toast} from '@utilities/toast'
import {AddAddressSection} from './add-address-section'
import {
    SETTINGS_AREA_DASHED_BORDER_BACKGROUND,
    SETTINGS_SECTION_WRAPPER_CLASSNAME
} from '@features/settings/constants.ts'

export const Availability = () => {
    const {t} = useTranslation()
    const userMeQuery = useMe({throwOnError: true})
    const user = userMeQuery.data ?? raise('user is nullish')
    const userAddress = user.address
    const userLatitude = user.latitude
    const userLongitude = user.longitude
    const isCompleted = !!userAddress && !!userLatitude && !!userLongitude
    const areasData = user?.photographerGeos ?? []
    const city = areasData.find(area => area.isMain)?.geo.geo
    const countries = [...new Set(areasData.map(geo => geo.geo && geo.geo.country))]

    const completedWrapperRef = useRef<HTMLDivElement>(null)
    const settingsCardRef = useRef<HTMLDivElement>(null)

    const [deletingAreaId, setDeletingAreaId] = useState<number | null>(null)
    const changeFreelancerAvailabilityMutation = useChangeFreelancerAvailability({userId: user.id})
    const changeFreelancerAddressMutation = useChangeFreelancerAddress({
        userId: user.id,
        onSuccess: () => toast.success(t('settings:personal_information:add_address_success'))
    })
    const deleteFreelancerAreaMutation = useDeleteFreelancerArea({
        onSettled: () => setDeletingAreaId(null)
    })
    const handlerDeleteArea = (areaId: number) => {
        setDeletingAreaId(areaId)
        deleteFreelancerAreaMutation.mutate({areaId, userId: user.id})
    }

    const onSelectAddressAutocomplete = (fullAddressValue: google.maps.GeocoderResult, latLng: Coordinates) => {
        const {country} = retrieveAddressDataFromComponents(fullAddressValue.address_components)

        changeFreelancerAddressMutation.mutate({
            ...latLng,
            address: fullAddressValue.formatted_address,
            country
        })
    }

    return (
        <div className={SETTINGS_SECTION_WRAPPER_CLASSNAME}>
            <CategoryCard
                icon={Briefcase01Icon}
                title={t('commons:availability_alert_title')}
                description={t('settings:personal_information:availability_description')}
            />

            <div className="flex flex-col gap-6">
                <Switch
                    id="availability"
                    labelPosition="right"
                    label={t('settings:available')}
                    checked={user.shootAvailable}
                    onChange={
                        changeFreelancerAvailabilityMutation.isPending
                            ? undefined
                            : value =>
                                  changeFreelancerAvailabilityMutation.mutate({
                                      shoot_available: value,
                                      region: user.region
                                  })
                    }
                />

                {isCompleted ? (
                    <div
                        ref={completedWrapperRef}
                        className="relative flex w-full flex-col gap-1 rounded-xl border border-neutral-300 px-4 py-3 text-md [&_button]:whitespace-nowrap [&_h3]:font-medium md:w-fit"
                    >
                        <h3>{city}</h3>
                        <div className="flex flex-col items-start gap-2 md:flex-row md:items-center">
                            <p>{userAddress}</p>

                            <AddAddressSection
                                buttonText={t('settings:personal_information:change_address_action')}
                                disabled={changeFreelancerAddressMutation.isPending}
                                onSelectAddress={onSelectAddressAutocomplete}
                                anchor={completedWrapperRef}
                            />
                            {changeFreelancerAddressMutation.isPending && <Spinner size={16} />}
                        </div>
                    </div>
                ) : (
                    <SettingsCard
                        ref={settingsCardRef}
                        isCompleted={isCompleted}
                        className="relative w-full flex-col md:flex-col items-start md:items-start gap-2 [&_button]:whitespace-nowrap [&_h3]:font-medium"
                    >
                        <AlertCircleIcon fill="var(--color-warning-600)" width={20} height={20} />
                        <div className="flex flex-col gap-1">
                            <h3>
                                {t(
                                    userAddress
                                        ? 'settings:personal_information:add_coordinates_title'
                                        : 'settings:personal_information:add_address_title'
                                )}
                            </h3>
                            <p>
                                {t(
                                    userAddress
                                        ? 'settings:personal_information:add_coordinates_text'
                                        : 'settings:personal_information:add_address_text',
                                    {address: userAddress}
                                )}
                            </p>
                        </div>

                        <AddAddressSection
                            buttonText={t('settings:personal_information:add_address_action')}
                            disabled={changeFreelancerAddressMutation.isPending}
                            onSelectAddress={onSelectAddressAutocomplete}
                            anchor={settingsCardRef}
                        />
                        {changeFreelancerAddressMutation.isPending && <Spinner size={16} />}
                    </SettingsCard>
                )}

                {isCompleted &&
                    (userMeQuery.isPending ? (
                        <div className="flex w-full justify-center">
                            <Spinner size={20} />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4 [&_h3]:text-md [&_h3]:font-medium [&_p]:text-sm">
                            <div className="flex flex-col gap-2">
                                <h3>{t('settings:areas:areas_label')}</h3>
                                <p>{t('settings:areas:areas_description')}</p>
                            </div>
                            <div className="flex flex-wrap gap-3">
                                {areasData.map(
                                    area =>
                                        !area.isMain && (
                                            <AreaItem
                                                key={area.geoId}
                                                label={area.geo.geo}
                                                countryCode={countries.length > 1 ? area.geo.country : undefined}
                                                removeSelectedArea={() => {
                                                    handlerDeleteArea(area.geoId)
                                                }}
                                                isLoading={
                                                    deletingAreaId == area.geoId &&
                                                    deleteFreelancerAreaMutation.isPending
                                                }
                                            />
                                        )
                                )}
                                <AddAreasModal>
                                    <AreaItem
                                        className="cursor-pointer !border-none text-neutral-600"
                                        style={{backgroundImage: SETTINGS_AREA_DASHED_BORDER_BACKGROUND}}
                                    >
                                        <p>{t('settings:areas:add_areas')}</p>
                                        <PlusIcon size={20} />
                                    </AreaItem>
                                </AddAreasModal>
                            </div>
                        </div>
                    ))}
            </div>
        </div>
    )
}
